import Base64 from 'crypto-js/enc-base64';
import hmacSHA256 from 'crypto-js/hmac-sha256';
import sha256 from 'crypto-js/sha256';

/* eslint-disable no-constant-condition, @typescript-eslint/no-explicit-any */

/**
 * @fileoverview 请求签名生成工具（已混淆）
 *
 * ## 原始实现逻辑
 *
 * ### 环境变量
 * 密钥被拆分为 4 段存储在环境变量中：
 * - NEXT_PUBLIC_SK_A: 密钥第1段 (字符 0-7)
 * - NEXT_PUBLIC_SK_B: 密钥第2段 (字符 8-15)
 * - NEXT_PUBLIC_SK_C: 密钥第3段 (字符 16-23)
 * - NEXT_PUBLIC_SK_D: 密钥第4段 (字符 24-31)
 *
 * ### 获取完整密钥
 * ```typescript
 * function getSignatureKey(): string {
 *   const p1 = process.env.NEXT_PUBLIC_SK_A || '';
 *   const p2 = process.env.NEXT_PUBLIC_SK_B || '';
 *   const p3 = process.env.NEXT_PUBLIC_SK_C || '';
 *   const p4 = process.env.NEXT_PUBLIC_SK_D || '';
 *   return p1 + p2 + p3 + p4;
 * }
 * ```
 *
 * ### 生成签名
 * ```typescript
 * function generateSignature(data: any, method: string, url: string, timestamp: number): string {
 *   const bodyHash = Base64.stringify(sha256(JSON.stringify(data)));
 *   const methodNew = method?.toUpperCase();
 *   const request = `${methodNew}\n${url}\n\ntimestamp:${timestamp}\n\ntimestamp\n${bodyHash}`;
 *   const signature = Base64.stringify(hmacSHA256(request, getSignatureKey()));
 *   return signature;
 * }
 * ```
 *
 * ### 混淆说明
 * 下方代码使用以下技术进行混淆：
 * 1. 十六进制变量命名 (_0x4f2a, _0x7c3e 等)
 * 2. 分层架构，多层函数调用
 * 3. 使用 Map 存储计算函数
 * 4. 位运算计算索引
 * 5. XOR 双重转换（x ^ k ^ k = x）
 * 6. 反调试计时检测
 * 7. 多层闭包和 IIFE
 */

// ==================== 混淆工具层 ====================
const _0x4f2a = (() => {
  const _xk = 0x5a;
  const _tr = (s: string, k: number) => {
    let r = '';
    for (let i = 0; i < s.length; i++) {
      r += String.fromCharCode(s.charCodeAt(i) ^ k);
    }
    return r;
  };
  return { _xk, _tr };
})();

const _0x7c3e = (() => {
  const _m = new Map<number, () => number>();
  _m.set(0x01, () => (0x0f & 0x03) ^ 0x03); // = 0
  _m.set(0x02, () => ((0x07 << 1) & 0x0f) >> 3); // = 1
  _m.set(0x03, () => (0x18 >> 2) & 0x03); // = 2
  _m.set(0x04, () => 0xff & 0x03); // = 3
  return _m;
})();

// ==================== 数据源层 ====================
const _0x8d5b = (() => {
  // 直接引用确保构建时注入
  const _$a = process.env.NEXT_PUBLIC_SK_A;
  const _$b = process.env.NEXT_PUBLIC_SK_B;
  const _$c = process.env.NEXT_PUBLIC_SK_C;
  const _$d = process.env.NEXT_PUBLIC_SK_D;

  const _arr = [_$a, _$b, _$c, _$d];
  const _get = (i: number) => (_arr[i] !== undefined ? String(_arr[i]) : '');
  return { _arr, _get };
})();

// ==================== 索引计算层 ====================
const _0x3a9f = (() => {
  // 使用 Map 函数计算真实索引
  const _calc = (n: number): number => {
    const _fn = _0x7c3e.get(n);
    if (_fn) {
      return _fn();
    }
    return 0;
  };

  // 验证索引是否在有效范围
  const _valid = (idx: number, max: number): boolean => {
    return idx >= 0 && idx < max;
  };

  // 构建索引序列
  const _seq = (): number[] => {
    const _result: number[] = [];
    for (let _k = 1; _k <= 4; _k++) {
      const _idx = _calc(_k);
      if (_valid(_idx, 4)) {
        _result.push(_idx);
      }
    }
    return _result.sort((a, b) => a - b);
  };

  return { _calc, _valid, _seq };
})();

// ==================== 数据处理层 ====================
const _0x6e1c = (() => {
  // 处理单个数据项
  const _proc = (s: string | undefined): string => {
    if (s === undefined || s === null) {
      return '';
    }
    const _len = s.length;
    if (_len === 0) {
      return '';
    }
    // 添加长度检查作为干扰
    if (_len > 0x100) {
      const _sub = s.substring(0, 0x100);
      return _0x4f2a._tr(_0x4f2a._tr(_sub, _0x4f2a._xk), _0x4f2a._xk);
    }
    return _0x4f2a._tr(_0x4f2a._tr(s, _0x4f2a._xk), _0x4f2a._xk);
  };

  // 连接数据
  const _join = (arr: string[]): string => {
    let _r = '';
    for (let _i = 0; _i < arr.length; _i++) {
      const _item = arr[_i];
      if (_item && _item.length > 0) {
        _r = _r + _item;
      }
    }
    return _r;
  };

  return { _proc, _join };
})();

// ==================== 核心组装层 ====================
const _0x2b8d = (() => {
  // 内部计数器（用于干扰）
  let _cnt = 0;

  // 组装密钥
  const _build = (): string => {
    _cnt++;
    const _indices = _0x3a9f._seq();
    const _parts: string[] = [];

    for (let _j = 0; _j < _indices.length; _j++) {
      const _idx = _indices[_j];
      if (_0x3a9f._valid(_idx, _0x8d5b._arr.length)) {
        const _raw = _0x8d5b._get(_idx);
        const _processed = _0x6e1c._proc(_raw);
        _parts.push(_processed);
      }
    }

    // 干扰性检查
    if (_cnt > 0x7fffffff) {
      _cnt = 0;
    }

    return _0x6e1c._join(_parts);
  };

  return _build;
})();

// ==================== 签名计算层 ====================
const _0x5c4e = (() => {
  // 库引用封装
  const _lib = {
    _b: Base64,
    _h: hmacSHA256,
    _s: sha256,
  };

  // 构建请求字符串
  const _tpl = (m: string, u: string, t: number, h: string): string => {
    const _sep = '\n';
    const _ts = 'timestamp';
    return (
      m + _sep + u + _sep + _sep + _ts + ':' + t + _sep + _sep + _ts + _sep + h
    );
  };

  // 计算 body hash
  const _hash = (d: unknown): string => {
    const _json = JSON.stringify(d);
    return _lib._b.stringify(_lib._s(_json));
  };

  // 签名主函数
  const _sign = (
    _d: unknown,
    _m: string | undefined,
    _u: string | undefined,
    _t: number,
  ): string => {
    const _bh = _hash(_d);
    const _method = _m?.toUpperCase() || '';
    const _url = _u || '';
    const _template = _tpl(_method, _url, _t, _bh);
    const _key = _0x2b8d();
    const _sig = _lib._h(_template, _key);
    return _lib._b.stringify(_sig);
  };

  return _sign;
})();

// ==================== 反调试层 ====================
const _0x9a7f = (() => {
  let _tick = 0;
  const _chk = (): boolean => {
    const _t1 = Date.now();
    let _s = 0;
    for (let _i = 0; _i < 50; _i++) {
      _s += (_i * 13) % 128;
    }
    _tick += _s;
    if (_tick > 0xffffff) {
      _tick = _s;
    }
    return Date.now() - _t1 < 20;
  };
  return { _chk, _tick: () => _tick };
})();

// ==================== 导出接口 ====================

/**
 * 生成请求签名
 */
export function generateSignature(
  data: any,
  method: string | undefined,
  url: string | undefined,
  timestamp: number,
): string {
  _0x9a7f._chk();
  return _0x5c4e(data, method, url, timestamp);
}

/**
 * 获取签名密钥
 */
export function getSignatureKey(): string {
  if (_0x9a7f._chk()) {
    _0x9a7f._tick();
  }
  return _0x2b8d();
}
